import { useCallback } from "react";
import { useAccount } from "../../../context/AccountContext";

interface ApiCallOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: any;
  params?: Record<string, string>;
}

interface ApiResult<T = any> {
  ok: boolean;
  data?: T;
  error?: string;
}

export function useRechargeApi() {
  const { effectiveProxyBase, effectiveShop } = useAccount();

  const call = useCallback(async <T = any>(
    path: string,
    opts: ApiCallOptions = {}
  ): Promise<ApiResult<T>> => {
    const { method = "GET", body, params = {} } = opts;
    const searchParams = new URLSearchParams({ shop: effectiveShop, ...params });
    const url = `${effectiveProxyBase}${path}?${searchParams.toString()}`;

    const fetchOnce = async (): Promise<ApiResult<T>> => {
      const res = await fetch(url, {
        method,
        credentials: "include",
        ...(body ? {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        } : {}),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        const errMsg = typeof data.error === "string" ? data.error
          : typeof data.message === "string" ? data.message
          : data.errors?.join(", ") || `Request failed (${res.status})`;
        return { ok: false, error: errMsg };
      }
      return { ok: true, data };
    };

    try {
      return await fetchOnce();
    } catch (err: any) {
      // 1 retry on network failure only
      if (err?.name === "TypeError" || err?.message?.includes("fetch")) {
        try {
          return await fetchOnce();
        } catch (retryErr: any) {
          return { ok: false, error: retryErr?.message || "Network error after retry" };
        }
      }
      return { ok: false, error: err?.message || "Unknown error" };
    }
  }, [effectiveProxyBase, effectiveShop]);

  return { call };
}
